import React, { useState, useEffect } from 'react';
import { Search, Grid, List, RefreshCw, MapPin, Calendar, TrendingUp, Users, Target, CheckCircle, XCircle } from 'lucide-react';
import './PreSowing.css';

const SowingEmergence = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({
    totalActivities: 0,
    landPrepared: 0,
    notPrepared: 0,
    activeFarmers: 0,
    plannedArea: 0
  });

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    fetch(" https://sadaatagrofarms.com/api/crop_stages/sowing-emergence/", {
      headers: { 
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    })
      .then(res => res.json())
      .then(data => {
        const results = data.results || [];

        setActivities(results);

        const total = results.length;
        const prepared = results.filter(a => a.first_irrigation_applied === true).length;
        const notPrepared = results.filter(a => a.first_irrigation_applied === false).length;

        const uniqueFarmers = new Set(results.map(a => a.farmer));

        setStats({
          totalActivities: total,
          landPrepared: prepared,
          notPrepared,
          activeFarmers: uniqueFarmers.size,
          plannedArea: 0
        });
      })
      .catch(err => console.error("Fetch error:", err));
  }, []);

  const filteredActivities = activities.filter(a => {
    const s = searchQuery.toLowerCase();
    const matchesSearch =
      a.crop_name?.toLowerCase().includes(s) ||
      a.variety?.join(" ").toLowerCase().includes(s) ||
      String(a.farmer).includes(s);

    const matchesStatus =
      filterStatus === "All" ||
      (filterStatus === "Prepared" && a.first_irrigation_applied) ||
      (filterStatus === "Not Prepared" && !a.first_irrigation_applied);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="pre-sowing">

      {/* Stage Tabs */}
      <div className="stage-tabs">
        <button className="stage-tab">Pre-Sowing</button>
        <button className="stage-tab active">Sowing & Emergence</button>
        <button className="stage-tab">Emergence</button>
        <button className="stage-tab">Growth & Tillering</button>
        <button className="stage-tab">Booting Stage</button>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Sowing & Emergence Dashboard</h1>
          <p className="subtitle">Tracking sowing activities and emergence performance</p>
        </div>
        <button className="refresh-btn" onClick={() => window.location.reload()}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green"><TrendingUp size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalActivities}</div>
            <div className="stat-label">Total Records</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.landPrepared}</div>
            <div className="stat-label">Prepared Fields</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red"><XCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value danger">{stats.notPrepared}</div>
            <div className="stat-label">Not Prepared</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue"><Users size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeFarmers}</div>
            <div className="stat-label">Active Farmers</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="controls-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search crop, variety, farmer..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="view-toggle">
          <button className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>
            <Grid size={18} />
          </button>
          <button className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
            <List size={18} />
          </button>
        </div>

        <div className="status-filters">
          <button className={`filter-chip ${filterStatus === 'All' ? 'active' : ''}`} onClick={() => setFilterStatus('All')}>
            All
          </button>
          <button className={`filter-chip ${filterStatus === 'Prepared' ? 'active' : ''}`} onClick={() => setFilterStatus('Prepared')}>
            Prepared
          </button>
          <button className={`filter-chip ${filterStatus === 'Not Prepared' ? 'active' : ''}`} onClick={() => setFilterStatus('Not Prepared')}>
            Not Prepared
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="results-bar">
        <div className="results-count">
          Showing <strong>{filteredActivities.length}</strong> of <strong>{stats.totalActivities}</strong>
        </div>
      </div>

      {/* Cards */}
      <div className={`activities-${viewMode}`}>
        {filteredActivities.map(activity => (
          <div key={activity.id} className="activity-card">

            <div className="activity-header">
              <div className="activity-crop">
                🌾 <span>{activity.crop_name}</span>
              </div>

              <span className={`status-badge ${activity.first_irrigation_applied ? "prepared" : "not-prepared"}`}>
                {activity.first_irrigation_applied ? "Prepared" : "Not Prepared"}
              </span>
            </div>

            <div className="activity-location">
              <MapPin size={14} />
              <span>Farmer #{activity.farmer} • Farm #{activity.farm}</span>
            </div>

            <div className="activity-details">
              <div className="detail-row">
                <span className="detail-label">Variety</span>
                <span className="detail-value">{activity.variety?.join(", ")}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Seed Rate</span>
                <span className="detail-value">{activity.seed_rate}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Germination %</span>
                <span className="detail-value">{activity.germination_percentage}%</span>
              </div>
            </div>

            <div className="activity-footer">
              <Calendar size={14} />
              <span>Sowing Date</span>
              <strong>{activity.raya_sowing_date}</strong>
            </div>

          </div>
        ))}
      </div>
    </div>
  );
};

export default SowingEmergence;
